import { useState } from "react";
import type { Frist, FristAendern, Fristart } from "../api/typen";
import { betragEingabe } from "../format";

/** W82 Web-5c (K15): Texte und Grenzen des Blatts „Neue Frist“ (FristNeuBlatt.tsx). */

/** Länge des Titels wie der Server (`FristTitel`, 120 Zeichen nach dem Trimmen). */
export const TITEL_MAX = 120;
/** Texte des Blatts (auftrag-w82-web-5c, Tabelle „Texte“; wortgleich iOS und Android). */
export const FRIST_NEU_TEXTE = {
  titel: "Neue Frist",
  ohneBezug: "Gib einen Titel ein oder wähle eine Akte.",
  betrag: "Bitte als Betrag eingeben, etwa 21,05.",
  angelegt: "Frist angelegt.",
  datum: (von: string, bis: string) => `Bitte ein Datum zwischen ${von} und ${bis}.`,
};

/** W82 Web-5h1: Texte des Blatts „Frist“ und der Rückfrage (Satzquelle `satzquelle-5h-s2.md` Z03 bis Z13, wortgleich iOS und
 *  Android). */
export const FRIST_TEXTE = {
  titel: "Frist",
  geaendert: "Frist geändert.",
  fuerWen: "Für wen",
  gemeinsam: "Gemeinsam",
  niemand: "Niemand bestimmtes",
  fuerWenBrief: (name: string) => `Für wen: ${name}, wie der Brief`,
  ausBrief: (text: string) => `Aus Brief: ${text}`,
  briefOeffnen: "Brief öffnen",
  loeschenUnten: "Frist löschen",
  loeschenTitel: "Frist löschen?",
  loeschenBrief: "Die Frist wird entfernt. Auch wenn der Brief neu eingelesen wird, kommt sie nicht wieder.",
  loeschen: "Die Frist wird entfernt.",
  behalten: "Behalten",
  loeschenKnopf: "Löschen",
  geloescht: "Gelöscht.",
  /** Z12: Lesetext der Fläche einer Fristzeile, hinter dem Inhalt der Zeile */
  details: ", Details",
  /** Z16: Knöpfe im Detail ab 1280 px */
  aendern: "Ändern",
  loeschenDetail: "Löschen",
  /** Z14, Z15: Reiter Fristen leer bei gewählter Person */
  leerTitel: (vorname: string) => `Keine offenen Fristen für ${vorname}`,
  leerText: "Alle Fristen siehst du mit „Alle zeigen“.",
} as const;

/** Grenzen wie der Server (`dienste/fristen.py` `datum_erlaubt`): heute minus ein Jahr bis heute plus zehn Jahre; vom 29. Februar
 *  aus der 28., wenn das Zieljahr keinen 29. hat. Gerechnet mit dem Tag in Europe/Berlin. */
export function fristGrenzen(heute: string): { von: string; bis: string } {
  const [j, m, t] = heute.split("-").map(Number) as [number, number, number];
  const schalt = (x: number) => (x % 4 === 0 && x % 100 !== 0) || x % 400 === 0;
  const jahr = (n: number) => {
    // wie `date.replace(year=…)`: gibt es den 29. Februar im Zieljahr nicht, der 28.
    const tag = m === 2 && t === 29 && !schalt(j + n) ? 28 : t;
    return `${String(j + n).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(tag).padStart(2, "0")}`;
  };
  return { von: jahr(-1), bis: jahr(10) };
}

/** Werte der Felder (Text wie im Formular): Betrag mit Komma, Akte und Person als Kennung, leer heißt keine Akte bzw. gemeinsam. */
export type Werte = { titel: string; art: Fristart; tag: string; betrag: string; akteId: string; personId: string };
export const LEER: Werte = { titel: "", art: "zahlen", tag: "", betrag: "", akteId: "", personId: "" };

export function werteVon(f: Frist): Werte {
  return {
    titel: f.titel ?? "",
    art: f.art,
    tag: f.datum,
    betrag: f.betrag ? Number(f.betrag).toFixed(2).replace(".", ",") : "",
    akteId: f.akte_id ?? "",
    personId: f.person_id ?? "",
  };
}

/** Nur die geänderten Felder einer bestehenden Frist (K45: `model_fields_set` des Servers): leerer Betrag `null`, „Gemeinsam“
 *  `person_id: null`; Titel, Akte und Person nur ohne Brief (der Brief bestimmt sie, sonst 422), die Person nur ab zwei Personen. */
export function fristAenderung(f: Frist, w: Werte, mitPerson: boolean): FristAendern {
  const k: FristAendern = {};
  if (w.art !== f.art) k.art = w.art;
  if (w.tag !== f.datum) k.datum = w.tag;
  const betragNeu = w.betrag.trim() ? betragEingabe(w.betrag) : null;
  if ((betragNeu === null ? null : Number(betragNeu)) !== (f.betrag ? Number(f.betrag) : null)) k.betrag = betragNeu;
  if (!f.dokument_id) {
    const titel = w.titel.trim() || null;
    if (titel !== (f.titel ?? null)) k.titel = titel;
    if ((w.akteId || null) !== (f.akte_id ?? null)) k.akte_id = w.akteId || null;
    if (mitPerson && (w.personId || null) !== (f.person_id ?? null)) k.person_id = w.personId || null;
  }
  return k;
}

/** Zustand des Blatts „Frist“ bei einem Aufrufer: die Frist bleibt beim Schließen stehen, damit Titel und Felder nicht während
 *  des Ausfederns auf „Neue Frist“ wechseln. */
export function useFristBlatt(): { offen: boolean; frist: Frist | undefined; oeffnen: (f: Frist) => void; schliessen: () => void } {
  const [stand, setStand] = useState<{ offen: boolean; frist: Frist | undefined }>({ offen: false, frist: undefined });
  return {
    offen: stand.offen,
    frist: stand.frist,
    oeffnen: (f) => setStand({ offen: true, frist: f }),
    schliessen: () => setStand((alt) => ({ ...alt, offen: false })),
  };
}
